import React, { useEffect, useState, useCallback } from "react";
import {
  Phone, PhoneCall, Loader2, Languages, RefreshCw, Sparkles, PlayCircle,
  Clock, Link2, Link2Off, ShieldCheck, ShieldAlert,
} from "lucide-react";
import { api } from "../api";
import { CallRow } from "./crm/AIVoiceAgentView";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

function Kpi({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 p-4 bg-white">
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-slate-400 font-medium">
        {Icon && <Icon size={11} />} {label}
      </div>
      <div className="text-2xl font-semibold text-slate-800 mt-1.5">{value}</div>
    </div>
  );
}

export default function AIVoiceAgent() {
  const [calls, setCalls] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [demoRunning, setDemoRunning] = useState(false);
  const [demoResult, setDemoResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a, s] = await Promise.all([
        api.get("/crm/ai-calls"),
        api.get("/crm/ai-calls/analytics"),
        api.get("/crm/ai-voice-settings"),
      ]);
      setCalls(c.data || []);
      setAnalytics(a.data || null);
      setSettings(s.data || null);
    } catch (e) { console.warn("[AIVoiceAgent] load failed", e?.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSetting = async (key) => {
    if (!settings) return;
    setSaving(true);
    try {
      const { data } = await api.put("/crm/ai-voice-settings", {
        auto_call_enabled: key === "auto_call_enabled" ? !settings.auto_call_enabled : settings.auto_call_enabled,
        crm_connected: key === "crm_connected" ? !settings.crm_connected : settings.crm_connected,
        languages: settings.languages,
      });
      setSettings(data);
    } catch (e) { console.warn(e); }
    setSaving(false);
  };

  const runDemoCall = async () => {
    setDemoRunning(true);
    setDemoResult(null);
    try {
      const { data } = await api.post("/voice-agent/demo-call");
      setDemoResult(data);
      load();
    } catch (e) {
      setDemoResult({ error: e?.response?.data?.detail || e.message });
    }
    setDemoRunning(false);
  };

  if (loading && !analytics) {
    return <div className="h-full grid place-items-center text-slate-400"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div data-testid="ai-voice-agent-app" className="h-full overflow-y-auto p-5 md:p-6 space-y-5" style={{ background: "#ffffff" }}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <Phone size={19} className="text-emerald-600" /> AI Voice Calling Agent
          </h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            Standalone outbound voice agent · {settings?.engine === "elevenlabs" ? "ElevenLabs" : "OpenAI TTS/Whisper"} engine ·{" "}
            {settings?.crm_connected ? "linked to HEXA CRM" : "running independently"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="ai-voice-app-demo-call" onClick={runDemoCall} disabled={demoRunning}
            className="text-[11.5px] px-3.5 py-2 rounded-lg flex items-center gap-1.5 text-white transition disabled:opacity-60"
            style={{ background: "linear-gradient(120deg,#16a34a,#22c55e)" }}>
            {demoRunning ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />} Run Demo Call
          </button>
          <button data-testid="ai-voice-app-refresh" onClick={load}
            className="text-[11px] px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-1.5">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {demoResult && (
        <div data-testid="ai-voice-demo-result" className={`rounded-xl border p-4 space-y-1.5 ${demoResult.error ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50"}`}>
          {demoResult.error ? (
            <div className="text-[12.5px] text-rose-600">Demo call failed: {demoResult.error}</div>
          ) : (
            <>
              <div className="text-[12.5px] font-semibold text-emerald-800 flex items-center gap-1.5">
                <PhoneCall size={13} /> Demo call completed with {demoResult.lead_name} · {demoResult.phone}
              </div>
              <div className="text-[12px] text-slate-700 whitespace-pre-line">{demoResult.transcript}</div>
              {demoResult.recording_url && (
                <audio data-testid="ai-voice-demo-audio" controls
                  src={`${demoResult.recording_url}${demoResult.recording_url.includes("?") ? "&" : "?"}token=${encodeURIComponent(localStorage.getItem("aura_token") || "")}`}
                  className="w-full h-8 mt-1" />
              )}
              <div className="flex items-center gap-3 text-[11.5px] text-slate-600 pt-1">
                <span>Score: <b>{demoResult.ai_score}/100</b></span>
                <span className="flex items-center gap-1">
                  {demoResult.verified ? <ShieldCheck size={12} className="text-emerald-600" /> : <ShieldAlert size={12} className="text-amber-500" />}
                  {demoResult.verified ? "Verified" : "Unverified"}
                </span>
                <span>Interest: <b>{demoResult.interest_level}</b></span>
                <span>{demoResult.duration_sec}s</span>
              </div>
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi testid="ai-voice-app-kpi-calls" label="Total Calls" value={analytics?.total ?? 0} icon={PhoneCall} />
        <Kpi testid="ai-voice-app-kpi-minutes" label="Consumption of Minutes" value={analytics?.total_minutes ?? 0} icon={Clock} />
        <Kpi testid="ai-voice-app-kpi-connect" label="Connect Rate" value={`${analytics?.connect_rate ?? 0}%`} />
        <Kpi testid="ai-voice-app-kpi-verified" label="Verified Rate" value={`${analytics?.verified_rate ?? 0}%`} />
        <Kpi testid="ai-voice-app-kpi-score" label="Avg Score" value={`${analytics?.avg_score ?? 0}/100`} />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 rounded-xl border border-slate-200 p-4 bg-white">
          <div className="text-[12px] font-semibold text-slate-700 mb-2">Calls — last 7 days</div>
          <div style={{ width: "100%", height: 170 }}>
            <ResponsiveContainer>
              <BarChart data={analytics?.trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#16a34a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 p-4 bg-white space-y-3">
          <div className="text-[12px] font-semibold text-slate-700">Settings</div>
          <div className="flex items-center justify-between">
            <span className="text-[12px] text-slate-600 flex items-center gap-1.5">
              {settings?.crm_connected ? <Link2 size={12} className="text-emerald-600" /> : <Link2Off size={12} className="text-slate-400" />}
              Connect to HEXA CRM
            </span>
            <button data-testid="ai-voice-app-crm-toggle" onClick={() => toggleSetting("crm_connected")} disabled={saving}
              role="switch" aria-checked={!!settings?.crm_connected}
              className={`relative rounded-full transition ${settings?.crm_connected ? "bg-emerald-500" : "bg-slate-300"}`}
              style={{ height: 22, width: 40 }}>
              <span className="absolute top-0.5 left-0.5 rounded-full bg-white transition"
                style={{ transform: settings?.crm_connected ? "translateX(18px)" : "translateX(0)", width: 18, height: 18 }} />
            </button>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[12px] text-slate-600">Auto-call new ad leads</span>
            <button data-testid="ai-voice-app-autocall-toggle" onClick={() => toggleSetting("auto_call_enabled")} disabled={saving || !settings?.crm_connected}
              role="switch" aria-checked={!!settings?.auto_call_enabled}
              className={`relative rounded-full transition disabled:opacity-40 ${settings?.auto_call_enabled ? "bg-emerald-500" : "bg-slate-300"}`}
              style={{ height: 22, width: 40 }}>
              <span className="absolute top-0.5 left-0.5 rounded-full bg-white transition"
                style={{ transform: settings?.auto_call_enabled ? "translateX(18px)" : "translateX(0)", width: 18, height: 18 }} />
            </button>
          </div>
          <div className="flex items-center justify-between text-[12px]">
            <span className="text-slate-600">Voice engine</span>
            <span className={`font-medium ${settings?.elevenlabs_configured ? "text-emerald-600" : "text-amber-600"}`}>
              {settings?.elevenlabs_configured ? "ElevenLabs" : "OpenAI (fallback)"}
            </span>
          </div>
          <div className="flex items-center justify-between text-[12px]">
            <span className="text-slate-600">Telephony (DialNexa)</span>
            <span className={`font-medium ${settings?.dialnexa_configured ? "text-emerald-600" : "text-rose-500"}`}>
              {settings?.dialnexa_configured ? "Connected" : "Not connected"}
            </span>
          </div>
          <div className="pt-1 border-t border-slate-100">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-medium mb-1.5 flex items-center gap-1">
              <Languages size={11} /> Languages
            </div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(settings?.language_labels || {}).map(([code, label]) => (
                <span key={code} data-testid={`ai-voice-app-lang-${code}`}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{label}</span>
              ))}
            </div>
          </div>
          {!settings?.dialnexa_configured && (
            <div className="text-[10.5px] text-amber-600 flex items-start gap-1 pt-1">
              <Sparkles size={11} className="mt-0.5 shrink-0" /> Add a DialNexa key in Admin AI → Integrations for real outbound calls — use "Run Demo Call" until then.
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="text-[12px] font-semibold text-slate-700 mb-2">Call logs {analytics?.demo_count ? `· ${analytics.demo_count} demo` : ""}</div>
        {calls.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-[13px] border border-dashed border-slate-200 rounded-lg">
            No calls yet — click "Run Demo Call" above to see the full pipeline instantly.
          </div>
        ) : (
          <div className="space-y-2">
            {calls.map((c) => <CallRow key={c.id} call={c} />)}
          </div>
        )}
      </div>
    </div>
  );
}
